/**
 * range-main.js
 * Range Supplements — Theme JavaScript
 * Handles: Klaviyo email capture, scroll reveal, dark mode toggle
 *
 * Place in: Assets/range-main.js
 */

(function () {
  'use strict';

  /* ──────────────────────────────────────────────────────────
     CONFIG
  ────────────────────────────────────────────────────────── */
  const KLAVIYO_COMPANY_ID = 'RHz95f';
  const KLAVIYO_LIST_ID    = 'TQgMVR';
  const KLAVIYO_API_URL    = `https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`;

  /* ──────────────────────────────────────────────────────────
     KLAVIYO EMAIL CAPTURE
     Finds all [data-klaviyo-form] forms and wires up submission.
  ────────────────────────────────────────────────────────── */

  /**
   * Submit an email address to Klaviyo via the Client API.
   * @param {string} email
   * @returns {Promise<{ok: boolean, message: string}>}
   */
  async function subscribeToKlaviyo(email) {
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          list_id: KLAVIYO_LIST_ID,
          email: email,
          consented_at: new Date().toISOString(),
        },
      },
    };

    try {
      const response = await fetch(KLAVIYO_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type':  'application/json',
          'revision':      '2023-12-15',
          'Accept':        'application/json',
        },
        body: JSON.stringify(payload),
      });

      // Klaviyo returns 202 Accepted on success (no body)
      if (response.status === 202 || response.ok) {
        return { ok: true, message: "You're in. We'll be in touch soon." };
      }

      // Try to parse an error message
      let errorText = `Error ${response.status}`;
      try {
        const data = await response.json();
        if (data.errors && data.errors[0] && data.errors[0].detail) {
          errorText = data.errors[0].detail;
        }
      } catch (_) { /* ignore parse errors */ }

      return { ok: false, message: errorText };

    } catch (networkError) {
      console.error('[Range] Klaviyo fetch error:', networkError);
      return {
        ok: false,
        message: 'Something went wrong. Please try again.',
      };
    }
  }

  /**
   * Validate an email address (basic).
   * @param {string} email
   * @returns {boolean}
   */
  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
  }

  /**
   * Wire up a single Klaviyo form.
   * @param {HTMLFormElement} form
   */
  function initKlaviyoForm(form) {
    const input  = form.querySelector('input[type="email"]');
    const btn    = form.querySelector('button[type="submit"]');
    // Status element lives outside the form, keyed by data attribute or fallback
    const statusId = form.id
      ? form.id.replace('-form', '-form-status')
      : null;
    const status = statusId ? document.getElementById(statusId) : null;

    if (!input || !btn) return;

    function setStatus(message, type) {
      if (!status) return;
      status.textContent = message;
      status.className   = 'range-form__status';
      if (type) status.classList.add(`range-form__status--${type}`);
    }

    function setLoading(isLoading) {
      btn.disabled = isLoading;
      btn.textContent = isLoading ? 'Joining…' : btn.dataset.originalText || btn.textContent;
      if (!btn.dataset.originalText && !isLoading) return;
      if (!btn.dataset.originalText) {
        btn.dataset.originalText = btn.textContent;
      }
    }

    form.addEventListener('submit', async function (e) {
      e.preventDefault();

      const email = input.value.trim();

      // Validate
      if (!isValidEmail(email)) {
        setStatus('Please enter a valid email address.', 'error');
        input.focus();
        return;
      }

      // Submit
      setLoading(true);
      setStatus('');

      const result = await subscribeToKlaviyo(email);

      setLoading(false);

      if (result.ok) {
        setStatus(result.message, 'success');
        form.reset();
        // Optionally hide the form and show only the success message
        form.style.display = 'none';
      } else {
        setStatus(result.message, 'error');
      }
    });

    // Clear error on input
    input.addEventListener('input', function () {
      if (status && status.classList.contains('range-form__status--error')) {
        setStatus('');
      }
    });
  }

  /**
   * Initialize all Klaviyo forms on the page.
   */
  function initAllForms() {
    const forms = document.querySelectorAll('[data-klaviyo-form]');
    forms.forEach(initKlaviyoForm);
  }

  /* ──────────────────────────────────────────────────────────
     SCROLL REVEAL
     Uses IntersectionObserver to add .is-visible to .reveal elements.
  ────────────────────────────────────────────────────────── */

  function initScrollReveal() {
    // Bail if prefers-reduced-motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.querySelectorAll('.reveal').forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    if (!('IntersectionObserver' in window)) {
      // Fallback: just show everything
      document.querySelectorAll('.reveal').forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    const observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target); // fire once
          }
        });
      },
      {
        threshold: 0.12,
        rootMargin: '0px 0px -40px 0px',
      }
    );

    document.querySelectorAll('.reveal').forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ──────────────────────────────────────────────────────────
     DARK MODE TOGGLE
     Looks for a [data-theme-toggle] button anywhere on the page.
     Persists preference to localStorage.
  ────────────────────────────────────────────────────────── */

  function initDarkMode() {
    const toggle = document.querySelector('[data-theme-toggle]');
    if (!toggle) return;

    const stored = localStorage.getItem('range-theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const initialTheme = stored || (prefersDark ? 'dark' : 'light');

    document.documentElement.setAttribute('data-theme', initialTheme);
    updateToggleLabel(toggle, initialTheme);

    toggle.addEventListener('click', function () {
      const current = document.documentElement.getAttribute('data-theme');
      const next    = current === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      localStorage.setItem('range-theme', next);
      updateToggleLabel(toggle, next);
    });
  }

  function updateToggleLabel(btn, theme) {
    btn.setAttribute('aria-label', theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode');
    btn.setAttribute('data-current-theme', theme);
  }

  /* ──────────────────────────────────────────────────────────
     TICKER PAUSE ON HOVER
     Handled in CSS via animation-play-state: paused, but
     we add keyboard support here.
  ────────────────────────────────────────────────────────── */

  function initTickerAccessibility() {
    const ticker = document.querySelector('.range-ticker__track');
    if (!ticker) return;

    // Allow keyboard focus to pause the ticker
    ticker.setAttribute('tabindex', '0');
    ticker.addEventListener('focus', function () {
      ticker.style.animationPlayState = 'paused';
    });
    ticker.addEventListener('blur', function () {
      ticker.style.animationPlayState = '';
    });
  }

  /* ──────────────────────────────────────────────────────────
     SHOPIFY SECTION EVENTS
     Re-initialize when sections are added/removed in the editor.
  ────────────────────────────────────────────────────────── */

  function handleSectionLoad(event) {
    // Re-init forms within the loaded section
    const section = event.target;
    if (!section) return;
    const forms = section.querySelectorAll('[data-klaviyo-form]');
    forms.forEach(initKlaviyoForm);
  }

  /* ──────────────────────────────────────────────────────────
     INIT
  ────────────────────────────────────────────────────────── */

  function init() {
    initAllForms();
    initScrollReveal();
    initDarkMode();
    initTickerAccessibility();

    // Shopify theme editor events
    document.addEventListener('shopify:section:load', handleSectionLoad);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();